import "./base/styles/tiptap-styles.css";

import * as React from "react";

import {
  DepartmentOrderRemarkType,
  OrderNoteFileUploads,
  ProviderReferralStatus,
} from "./__generated__/graphql";
import { Fragment, useEffect, useRef, useState } from "react";
import {
  IssueNoteResponse,
  NoteTemplateResponseOption,
  OrderNoteUploadFileTypes,
  UserInfo,
} from "./__generated__/graphql";
import { getInitials, userDisplayLabel } from "./utils/formatting";
import { useLazyQuery, useMutation } from "@apollo/client";

import DOMPurify from "dompurify";
import FileUploadCategorizationModal from "./FileUploadCategorizationModal";
import { P1B } from "./base/TextComponents/P1B";
import { P2 } from "./base/TextComponents/P2";
import { P2B } from "./base/TextComponents/P2B";
import SelectedFile from "./SelectedFile";
import { SelectedTemplateNames } from "./SelectedTemplateNames";
import { StandardButton } from "./buttons/StandardButton";
import StarterKit from "@tiptap/starter-kit";
import { TextButton } from "./buttons/TextButton";
import cloneDeep from "lodash/cloneDeep";
import { cn } from "./ClassName";
import { generateHTML } from "@tiptap/html";
import { gql } from "./__generated__";
import { isVerseOs } from "./utils/envConfig";

const CREATE_ORDER_NOTE_RESPONSE = gql(`
  mutation CREATE_ORDER_NOTE_RESPONSE(
    $departmentOrderId: Int!
    $orderNoteId: Int!
    $responseText: String!
    $fileAttachments: [OrderNoteFileUploads!]!
    $requireClinicResponse: Boolean!
    $llmChosenResponse: String!
    $opsUserId: Int
    $lastSeenOrderNoteId: Int
  ) {
    createOrderNoteResponse(
      departmentOrderId: $departmentOrderId
      orderNoteId: $orderNoteId
      responseText: $responseText
      fileAttachments: $fileAttachments
      requireClinicResponse: $requireClinicResponse
      llmChosenResponse: $llmChosenResponse
      opsUserId: $opsUserId
      lastSeenOrderNoteId: $lastSeenOrderNoteId
    ) {
      id
      note
      createdAt
      email
      clinicianName
      opsUserName
      fileAttachments {
        fileUrl
        fileName
        fileType
      }
      departmentOrderRemarks
      orderStatus
    }
  }
`);

const ISSUE_RESPONSE = gql(`
  mutation ISSUE_RESPONSE(
    $orderNumber: String!
    $noteId: Int!
    $approved: Boolean!
    $selections: [NoteResponseSelectionInput!]
  ) {
    issueResponse(
      orderNumber: $orderNumber
      noteId: $noteId
      approved: $approved
      selections: $selections
    ) {
      departmentOrderRemarks
      orderStatus
    }
  }
`);

const CANCEL_AUTOMATIC_REMINDERS = gql(`
  mutation CancelAutomaticReminders(
    $orderNoteId: Int!
  ) {
    cancelAutomaticReminders(
      orderNoteId: $orderNoteId
    )
  }
`);

const DRAFT_OPS_NOTE_AUTOCOMPLETE = gql(`
  query DRAFT_OPS_NOTE_AUTOCOMPLETE(
    $orderNoteId: Int!
    $orderNumber: String!
    $currentDraftMessage: String!
  ) {
    draftOpsNoteAutocomplete(
      orderNoteId: $orderNoteId
      orderNumber: $orderNumber
      currentDraftMessage: $currentDraftMessage
    )
  }`);

const RESOLVE_ORDER_NOTE = gql(`
  mutation ResolveOrderNote($noteId: Int!) {
    resolveOrderNote(noteId: $noteId) {
      departmentOrderRemarks
      orderStatus
    }
  }
`);

const RESOLVE_CLINIC_NOTE_THREAD = gql(`
  mutation ResolveClinicNoteThread($orderNumber: String!, $noteId: Int!) {
    resolveClinicNoteThread(orderNumber: $orderNumber, noteId: $noteId)
  }
`);

export function formatDateString(date: string): string {
  let dateObject = new Date(date);
  // Checks if string has timezone specified
  if (date.length && date[date.length - 1] !== "Z") {
    dateObject = new Date(date + "Z");
  }

  const dtFormat = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  });

  return dtFormat.format(dateObject);
}

function approvedAtLabel(createdAt: string) {
  const date = new Date(createdAt);
  return (
    date.toLocaleString("default", { month: "short" }) + " " + date.getDate()
  );
}

interface Props {
  orderNumber: string;
  note: any;
  setOrderStatus?: (val: any) => void;
  isOpsView?: boolean;
  updateSingleOrderRemark?: (
    remark: DepartmentOrderRemarkType,
    val: boolean,
  ) => void;
  setOrderRemarks?: (val: any) => void;
  onNoteReply?: (noteId?: number, newResponse?: any) => void;
  // IDs of every note and reply on the page. When set, the server rejects a
  // reply if the order has messages the page hasn't loaded.
  lastSeenOrderNoteId?: number;
  currentUserInfo: UserInfo;
  opsUserName?: string;
  onNoteResolved?: () => void;
  className?: string;
  renderQuickReplyButton?: (args: {
    onRespond: () => void;
    children: React.ReactNode;
  }) => React.ReactNode;
  renderApproveRejectButtons?: (args: {
    onApprove: () => void;
    onReject: () => void;
    disabled: boolean;
    approveLoading: boolean;
    rejectLoading: boolean;
  }) => React.ReactNode;
}

export function ProviderNote(props: Props) {
  const [message, setMessage] = useState("");
  const [isApproved, setIsApproved] = useState<boolean>(false);
  const [noteResponse, setNoteResponse] = useState("");
  const [responseErrorMessage, setResponseErrorMessage] = useState("");
  // Which option button is mid-flight, so only that one shows a spinner.
  const [pendingOptionId, setPendingOptionId] = useState<number | null>(null);
  const [note, setNote] = useState(props.note);
  const [showResponses, setShowResponses] = useState(
    props.note && props.note.responses.length > 2 ? false : true,
  );
  const [automaticClinicReminders, setAutomaticClinicReminders] = useState(
    props.note.automaticClinicReminders,
  );
  const [files, setFiles] = useState<File[]>([]);
  const [filesWithTypes, setFilesWithTypes] = useState<Record<string, File[]>>(
    {},
  );
  const [hideModal, setHideModal] = useState(true);
  const [uploadRequestString, setUploadRequestString] = useState("");
  const [sending, setSending] = useState<boolean>(false);
  const [noteLLMDraft, setNoteLLMDraft] = useState("");
  const [llmDraftPanelOpen, setLLMDraftPanelOpen] = useState(false);
  const [llmChosenResponse, setLLMChosenResponse] = useState("");
  const noteContentRef = useRef<HTMLDivElement>(null);
  const lightBoxRef = useRef<HTMLDivElement>(null);

  const filesRequested =
    note.patientDemographicsRequested ||
    note.providerNotesRequested ||
    note.orderCorrectionsRequested ||
    note.signatureRequested ||
    note.otherSupportingFilesRequested;

  React.useEffect(() => {
    const requestParts: string[] = [];
    if (note.patientDemographicsRequested) {
      requestParts.push("patient demographics");
    }
    if (note.providerNotesRequested) {
      requestParts.push("progress notes");
    }
    if (note.orderCorrectionsRequested) {
      requestParts.push("order corrections");
    }
    if (note.signatureRequested) {
      requestParts.push("signature");
    }
    if (note.otherSupportingFilesRequested) {
      requestParts.push("other supporting files");
    }

    let uploadRequestString = "";
    if (requestParts.length > 0) {
      uploadRequestString = requestParts.join(", ");
      if (requestParts.length === 2) {
        uploadRequestString = uploadRequestString.replace(",", " and");
      }
      if (requestParts.length > 2) {
        const index = uploadRequestString.lastIndexOf(",");
        uploadRequestString =
          uploadRequestString.substring(0, index) +
          ", and" +
          uploadRequestString.substring(index + 1);
      }

      setUploadRequestString(
        `Please use the "Add Files" button below to upload ${uploadRequestString}.`,
      );
    }
  }, []);

  const [orderNoteResponse, _] = useMutation(CREATE_ORDER_NOTE_RESPONSE, {
    fetchPolicy: "no-cache",
    onCompleted() {
      setNoteResponse("");
      setLLMChosenResponse("");
      setLLMDraftPanelOpen(false);
      setNoteLLMDraft("");
    },
  });

  const [issueResponseMutation, { loading: issueResponseLoading }] =
    useMutation(ISSUE_RESPONSE, {
      fetchPolicy: "no-cache",
    });

  const [cancellationResponse] = useMutation(CANCEL_AUTOMATIC_REMINDERS, {
    fetchPolicy: "no-cache",
  });

  const [draftOpsNoteAutocomplete, { loading: loadingAutoComplete }] =
    useLazyQuery(DRAFT_OPS_NOTE_AUTOCOMPLETE, {
      fetchPolicy: "no-cache",
      onCompleted(draftOpsNoteAutocompleteData) {
        if (draftOpsNoteAutocompleteData.draftOpsNoteAutocomplete) {
          setNoteLLMDraft(
            draftOpsNoteAutocompleteData.draftOpsNoteAutocomplete,
          );
          setLLMDraftPanelOpen(true);
        }
      },
    });

  const [resolveOrderNote, { loading: resolving }] =
    useMutation(RESOLVE_ORDER_NOTE);

  const [resolveClinicNoteThread, { loading: resolvingThread }] = useMutation(
    RESOLVE_CLINIC_NOTE_THREAD,
  );

  const cancelReminders = (checked: boolean) => {
    if (checked === false) {
      cancellationResponse({
        variables: {
          orderNoteId: note.postgresId,
        },
      })
        .then(() => {
          setAutomaticClinicReminders(false);
        })
        .catch(() => {
          alert("Failed to cancel automatic reminders.");
        });
    }
  };

  const submitNote = (inputNoteResponse: string) => {
    setSending(true);

    const fileAttachments: OrderNoteFileUploads[] = [];

    // Flatten all files and assign appropriate type
    Object.entries(filesWithTypes).forEach(([key, files]) => {
      files.forEach((file) => {
        const fileType = (() => {
          switch (key) {
            case "Progress Note":
              return OrderNoteUploadFileTypes.ProgressNote;
            case "Patient Demographics":
              return OrderNoteUploadFileTypes.PatientDemographics;
            case "Order Corrections":
              return OrderNoteUploadFileTypes.OrderCorrections;
            case "Other Supporting Files":
              return OrderNoteUploadFileTypes.OtherSupportingFiles;
            case "Signature":
              return OrderNoteUploadFileTypes.Signature;
            case "Unspecified":
              return OrderNoteUploadFileTypes.Unspecified;
            default:
              return OrderNoteUploadFileTypes.OtherSupportingFiles;
          }
        })();

        fileAttachments.push({
          file,
          fileType,
        });
      });
    });

    orderNoteResponse({
      variables: {
        departmentOrderId: note.departmentOrderId,
        orderNoteId: note.postgresId,
        responseText: inputNoteResponse,
        fileAttachments: fileAttachments,
        requireClinicResponse: requireClinicResponse,
        llmChosenResponse: llmChosenResponse,
        opsUserId: getLoggedInUser(),
        lastSeenOrderNoteId: props.lastSeenOrderNoteId,
      },
    })
      .then((result) => {
        const newNote = result.data?.createOrderNoteResponse;
        const newResponse = {
          id: newNote?.id,
          note: newNote?.note,
          createdAt: newNote?.createdAt,
          email: newNote?.email,
          fileAttachments: newNote?.fileAttachments,
          clinicianName: newNote?.clinicianName,
          opsUserName: newNote?.opsUserName,
        };
        setNote({
          ...note,
          responses: [...note.responses, newResponse],
          ...(requireClinicResponse && {
            isActionItem: true,
            opsResolvedAt: null,
          }),
        });
        setNoteResponse("");
        setShowResponses(true);
        setResponseErrorMessage("");
        setFiles([]);
        setFilesWithTypes({});
        setSending(false);
        props.onNoteReply?.(note.postgresId, {
          responses: [...note.responses, newResponse],
        });
        if (props.updateSingleOrderRemark) {
          props.updateSingleOrderRemark(
            DepartmentOrderRemarkType.NeedsYourResponse,
            requireClinicResponse,
          );
        }
        if (props.setOrderStatus) {
          if (props.isOpsView && requireClinicResponse) {
            props.setOrderStatus(ProviderReferralStatus.Issue);
          } else {
            props.setOrderStatus(
              result.data?.createOrderNoteResponse.orderStatus,
            );
          }
        }
        if (props.setOrderRemarks) {
          props.setOrderRemarks(
            result.data?.createOrderNoteResponse.departmentOrderRemarks,
          );
        }
      })
      .catch((error) => {
        // Some server errors are a JSON object of label -> message, e.g. when
        // new messages came in since the page loaded.
        let errorMessage = "Failed to submit note. Please try again.";
        try {
          errorMessage = Object.values(JSON.parse(error.message)).join(" ");
        } catch {
          // Not JSON; keep the generic message.
        }
        setResponseErrorMessage(errorMessage);
        console.log(error);
        setSending(false);
      });
  };

  const onNoteResponseCompleted = (
    response: IssueNoteResponse | null | undefined,
    message: string,
    approved: boolean,
    selectedTemplateKeyReplacementLabel?: string,
  ) => {
    if (props.setOrderStatus && response) {
      props.setOrderStatus(response.orderStatus);
    }
    if (props.setOrderRemarks && response) {
      props.setOrderRemarks(response.departmentOrderRemarks);
    }
    setMessage(message);
    props.onNoteReply?.(props.note.postgresId, {
      approved,
      approvedOrRejectedAt: new Date().toISOString(),
      ...(selectedTemplateKeyReplacementLabel !== undefined && {
        selectedTemplateKeyReplacementLabel,
      }),
    });
  };

  const onNoteResponseError = () => {
    setResponseErrorMessage(
      "An error occurred saving your response. You may need to refresh and try again.",
    );
  };

  const submitResponse = (approved: boolean) => {
    // Same double-submit guard as handleTemplateKeyResponse: one response per
    // note, and the buttons are disabled while this is in flight.
    if (issueResponseLoading) return;

    setResponseErrorMessage("");

    issueResponseMutation({
      variables: {
        noteId: props.note.postgresId,
        approved: approved,
        orderNumber: props.orderNumber,
      },
      onCompleted: (res) => {
        onNoteResponseCompleted(
          res.issueResponse,
          approved ? "Change approved." : "Change rejected.",
          approved,
        );
      },
      onError: onNoteResponseError,
    });
  };

  const handleNeedsApprovalResponse = (approved: boolean) => {
    submitResponse(approved);
    setIsApproved(approved);
  };

  const handleTemplateKeyResponse = (
    replacementId: number,
    responseOptionSpecId: number,
    label: string,
  ) => {
    // A second pick would be stored alongside the first, and the automation
    // would then treat both options as chosen and delete neither. The buttons
    // are disabled while in flight and replaced once answered; this also
    // catches a double-click landing before either takes effect.
    if (issueResponseLoading || pendingOptionId !== null) return;

    setResponseErrorMessage("");
    setPendingOptionId(replacementId);

    issueResponseMutation({
      variables: {
        noteId: props.note.postgresId,
        orderNumber: props.orderNumber,
        approved: true,
        selections: [
          {
            responseOptionSpecId,
            messageTemplateKeyReplacementId: replacementId,
          },
        ],
      },
      onCompleted: (res) => {
        setNote((prev: typeof props.note) => ({
          ...prev,
          selectedTemplateKeyReplacementLabel: label,
          approved: true,
          approvedOrRejectedAt: new Date().toISOString(),
        }));
        // Empty message: the render's `message` branch only understands the
        // legacy "Change approved."/"Change rejected." strings. The setNote
        // above already routes this to the same "Responded to send …" branch
        // a refreshed page uses.
        onNoteResponseCompleted(res.issueResponse, "", true, label);
        setPendingOptionId(null);
      },
      onError: () => {
        setPendingOptionId(null);
        onNoteResponseError();
      },
    });
  };

  const prettyFileType = (fileType: string) => {
    switch (fileType) {
      case "order-note-patient-demographics":
        return "Patient Demographics";
      case "order-note-progress-note":
        return "Progress Notes";
      case "order-note-order-corrections":
        return "Order Corrections";
      case "order-note-signature":
        return "Signature";
      case "order-note-supporting-files":
        return "Other Supporting Files";
      case "order-note-unspecified":
        return "Unspecified";
      default:
        return "Unlabeled File Type";
    }
  };

  const handleFileSelection = function (
    e: React.ChangeEvent<HTMLInputElement>,
  ) {
    e.preventDefault();
    const tempFiles: File[] = [...files];
    if (e.target.files) {
      for (let index = 0; index < e.target.files.length; index++) {
        const file = e.target.files.item(index);
        if (file) tempFiles.push(file);
      }
      setFiles(tempFiles);
    }
  };

  const getLoggedInUser = () => {
    if (isVerseOs()) {
      return (
        Number(localStorage.getItem("currentUser")) || props.currentUserInfo?.id
      );
    }
    return null;
  };

  useEffect(() => {
    if (files.length > 0) {
      setHideModal(false);
    }
  }, [files]);

  const [showLightBox, setShowLightBox] = useState(false);

  // Add expand-on-click logic for images
  useEffect(() => {
    // Get the container element
    const container = noteContentRef.current;
    if (!container) return;
    const lightbox = lightBoxRef.current;
    if (!lightbox) return;

    // Get all images in the container
    const imgs = container.querySelectorAll<HTMLImageElement>("img");
    imgs.forEach((img) => {
      img.onclick = null;
      img.removeAttribute("width");
      img.removeAttribute("style");
      img.className =
        "tw-w-[calc(33.33%-8px)] tw-h-auto tw-aspect-[4/3] tw-mr-[8px] tw-mt-[16px] tw-cursor-zoom-in tw-rounded-lg tw-border-solid tw-border-gray-light-1 tw-object-cover";
      img.onclick = (e) => {
        e.stopPropagation();
        const clone = img.cloneNode() as HTMLImageElement;
        clone.className = "tw-w-full tw-object-scale-down";
        lightbox.innerHTML = "";
        lightbox.appendChild(clone);
        setShowLightBox(true);
      };
    });
    // Cleanup: remove handlers on unmount
    return () => {
      imgs.forEach((img) => {
        if (img) {
          img.onclick = null;
        }
      });
    };
  }, [note.note]);

  /**
   * Used to quickly send a response/acknowledge a note
   */
  const thumbs_up = String.fromCodePoint(0x1f44d);

  /**
   * Used by the ops team to display the options selected when a note is sent
   * Shown only in verseos, not on versemedical.com
   */
  const noteCheckboxStyles = "tw-p-2 tw-ml-1 tw-mr-2 tw-select-none";
  let opsOptions: JSX.Element | null = null;
  if (props.isOpsView) {
    opsOptions = (
      <div className="tw-mt-[15px] tw-w-full tw-pl-[10px]">
        <label className="tw-mr-[15px]">
          <input
            type="checkbox"
            className={noteCheckboxStyles}
            checked={props.note.notifyEmail}
            disabled={true}
          />
          Notify Email?
        </label>
        <label className="tw-mr-[15px]">
          <input
            type="checkbox"
            className={noteCheckboxStyles}
            checked={props.note.notifyFax}
            disabled={true}
          />
          Notify Fax?
        </label>
        <label className="tw-mr-[15px]">
          <input
            type="checkbox"
            className={noteCheckboxStyles}
            checked={props.note.isIssue}
            disabled={true}
          />
          Issue?
        </label>
        <label className="tw-mr-[15px]">
          <input
            type="checkbox"
            className={noteCheckboxStyles}
            checked={props.note.needsApproval}
            disabled={true}
          />
          Needs Approval?
        </label>
        <hr className="tw-mt-2 tw-border-[#d5eeef]" />
        <div>
          <label className="tw-mr-[15px]">
            <input
              type="checkbox"
              className="tw-ml-1 tw-mr-2 tw-select-none tw-p-2"
              checked={automaticClinicReminders}
              onChange={(event) => cancelReminders(event.target.checked)}
              onKeyDown={(e) =>
                onEnter(e, () => cancelReminders(!automaticClinicReminders))
              }
              disabled={automaticClinicReminders === false}
            />
            Automatic Reminders?
          </label>
          <label className="tw-mr-[15px]">
            {props.note.maxClinicReminders} Reminders Max
          </label>
          <label className="tw-mr-[15px]">
            {props.note.clinicRemindersDaysApart} Days Apart
          </label>
          <label className="tw-mr-[15px]">
            {props.note.clinicRemindersSent} Reminders Sent
          </label>
        </div>
        <hr className="tw-mt-2 tw-border-[#d5eeef]" />
        <div className="tw-mb-[10px] tw-ml-[5px]">Requested Files?</div>
        <div>
          <label className="tw-mr-[15px]">
            <input
              type="checkbox"
              className={noteCheckboxStyles}
              checked={props.note.patientDemographicsRequested}
              disabled={true}
            />
            Patient Demographics
          </label>
          <label className="tw-mr-[15px]">
            <input
              type="checkbox"
              className={noteCheckboxStyles}
              checked={props.note.providerNotesRequested}
              disabled={true}
            />
            Progress Notes
          </label>
          <label className="tw-mr-[15px]">
            <input
              type="checkbox"
              className={noteCheckboxStyles}
              checked={props.note.orderCorrectionsRequested}
              disabled={true}
            />
            Order Corrections
          </label>
          <label className="tw-mr-[15px]">
            <input
              type="checkbox"
              className={noteCheckboxStyles}
              checked={props.note.signatureRequested}
              disabled={true}
            />
            Signature
          </label>
          <label className="tw-mr-[15px]">
            <input
              type="checkbox"
              className={noteCheckboxStyles}
              checked={props.note.otherSupportingFilesRequested}
              disabled={true}
            />
            Other Supporting Files
          </label>
        </div>
      </div>
    );
  }

  /**
   * Renders a message for approvals/rejections
   * @param props approval-related parameters (approved/reject, by who, when)
   * @returns message with an icon
   */
  const ApprovalMessage = (props: {
    approved: boolean;
    approvedOrRejectedBy?: string;
    approvedOrRejectedAt?: string;
  }) => {
    const approverName = userDisplayLabel(
      note.approvedOrRejectedBy?.name,
      note.approvedOrRejectedBy?.email,
    );
    return (
      <>
        <i
          className={`fa ${
            props.approved
              ? "fa-circle-check tw-text-blue-2"
              : "fa-circle-xmark tw-text-gray-4"
          } tw-mr-3 tw-text-[16px]`}
        ></i>
        This request has been {props.approved ? "approved" : "rejected"}
        {props.approvedOrRejectedBy && approverName
          ? " by " + approverName
          : ""}
        {props.approvedOrRejectedAt
          ? " on " + approvedAtLabel(props.approvedOrRejectedAt)
          : ""}
        .
        <TextButton text="Add comment" onClick={() => setAddComment(true)} />
      </>
    );
  };

  const noteDisplayName =
    userDisplayLabel(note.clinicianName, note.clinicianEmail) ??
    "Verse Medical";

  let initialShowAddComment = true;
  // Can approve/reject a note if applicable or display no actions otherwise
  let noteActions: JSX.Element | null = null;
  // An option is only actionable when it names the spec it answers; without
  // one the note falls back to plain approve/reject below.
  const templateResponseOptions: NoteTemplateResponseOption[] = (
    note.templateResponseOptions ?? []
  ).filter(
    (option: NoteTemplateResponseOption) => option.responseOptionSpecId != null,
  );

  if (message.length > 0) {
    noteActions =
      message === "Change approved." ? (
        <ApprovalMessage approved={true} />
      ) : (
        <ApprovalMessage approved={false} />
      );
  } else if (note.needsApproval && note.approved) {
    const approverName = userDisplayLabel(
      note.approvedOrRejectedBy?.name,
      note.approvedOrRejectedBy?.email,
    );
    const responseLabel = note.selectedTemplateKeyReplacementLabel;
    noteActions = (
      <>
        <i className="fa fa-circle-check tw-mr-3 tw-text-[16px] tw-text-blue-2"></i>
        {responseLabel ? (
          <>
            {approverName ? approverName + " responded" : "Responded"}
            {" to send " + responseLabel}
          </>
        ) : (
          <>
            This request has been approved
            {approverName ? " by " + approverName : ""}
          </>
        )}
        {approvedAtLabel(note.approvedOrRejectedAt)
          ? " on " + approvedAtLabel(note.approvedOrRejectedAt)
          : ""}
        .
      </>
    );
  } else if (note.needsApproval && note.approved === false) {
    const approverName = userDisplayLabel(
      note.approvedOrRejectedBy?.name,
      note.approvedOrRejectedBy?.email,
    );
    noteActions = (
      <>
        <i className="fa fa-circle-xmark tw-mr-3 tw-text-[16px] tw-text-gray-4"></i>
        This request has been rejected
        {approverName ? " by " + approverName : ""}
        {approvedAtLabel(note.approvedOrRejectedAt)
          ? " on " + approvedAtLabel(note.approvedOrRejectedAt)
          : ""}
        .
      </>
    );
  } else if (note.needsApproval) {
    // if note.approved is null (no response provided yet)
    initialShowAddComment = false;
    noteActions = (
      <>
        {templateResponseOptions.length > 0 ? (
          <>
            {templateResponseOptions.map((option) => (
              <StandardButton
                key={option.id}
                style="primary-blue"
                className="tw-mr-[8px]"
                onClick={() =>
                  handleTemplateKeyResponse(
                    option.id,
                    option.responseOptionSpecId!,
                    option.label,
                  )
                }
                disabled={issueResponseLoading}
                loading={issueResponseLoading && pendingOptionId === option.id}
              >
                {option.label}
              </StandardButton>
            ))}
          </>
        ) : props.renderApproveRejectButtons ? (
          props.renderApproveRejectButtons({
            onApprove: () => handleNeedsApprovalResponse(true),
            onReject: () => handleNeedsApprovalResponse(false),
            disabled: issueResponseLoading,
            approveLoading: issueResponseLoading && isApproved,
            rejectLoading: issueResponseLoading && !isApproved,
          })
        ) : (
          <>
            <StandardButton
              style="primary-blue"
              className="tw-mr-[8px]"
              onClick={() => handleNeedsApprovalResponse(true)}
              iconLeft="fas fa-check"
              disabled={issueResponseLoading}
              loading={issueResponseLoading && isApproved}
            >
              Approve
            </StandardButton>
            <StandardButton
              style="warning"
              onClick={() => handleNeedsApprovalResponse(false)}
              iconLeft="fa fa-xmark"
              disabled={issueResponseLoading}
              loading={issueResponseLoading && !isApproved}
            >
              Reject
            </StandardButton>
          </>
        )}
        <span className="tw-ml-4 tw-self-center tw-text-gray-3">or</span>
        <TextButton text="Add comment" onClick={() => setAddComment(true)} />
      </>
    );
  }
  const [addComment, setAddComment] = useState(initialShowAddComment);
  const [requireClinicResponse, setRequireClinicResponse] = useState(false);

  const getRawHTMLFromJSON = (json: any) => {
    let rawHTML = json;
    try {
      rawHTML = generateHTML(JSON.parse(json), [StarterKit]);
    } catch (error) {
      // It's probably already HTML
    }
    return rawHTML;
  };

  const isActiveActionItem = note.isActionItem && !note.opsResolvedAt;
  const isManuallyResolved = !!note.opsResolvedAt;
  const isResolvedByClinicResponse =
    !!note.clinicResponseRequiredAt &&
    !note.isActionItem &&
    !note.opsResolvedAt;

  const noteStyle = cn(
    "tw-border-[1px] tw-border-solid tw-my-[15px] tw-rounded-[8px] tw-text-[14px] tw-whitespace-pre-line tw-overflow-hidden tw-bg-white",
    isActiveActionItem && props.isOpsView
      ? "tw-border-red-1"
      : "tw-border-gray-light-1",
    props.className,
  );

  const getDraftLLMReply = (currentDraft?: string) => {
    draftOpsNoteAutocomplete({
      variables: {
        orderNoteId: note.postgresId,
        orderNumber: props.orderNumber,
        currentDraftMessage: currentDraft || "",
      },
    });
  };

  const draftMessage = () => {
    return (
      <>
        {llmDraftPanelOpen && (
          <div className="tw-m-3 tw-rounded-md tw-border-[1.5px] tw-border-solid tw-border-blue-2 tw-bg-blue-light-1 tw-p-3">
            <button
              className="hover:tw-text-bold tw-absolute tw-right-0 tw-top-0 tw-m-5 tw-cursor-pointer tw-border-none tw-bg-transparent tw-text-2xl tw-text-gray-1"
              onClick={() => {
                setNoteLLMDraft("");
                setLLMDraftPanelOpen(false);
              }}
            >
              x
            </button>
            <p className="tw-h-[120px] tw-cursor-default tw-select-none tw-overflow-auto tw-pr-4">
              {noteLLMDraft}
            </p>
            <StandardButton
              style="primary-blue"
              className="tw-mr-2 tw-h-[36px] tw-w-[130px]"
              onClick={() => {
                setNoteResponse(noteLLMDraft);
                setLLMChosenResponse(noteLLMDraft);
                setLLMDraftPanelOpen(false);
                setNoteLLMDraft("");
              }}
              disabled={loadingAutoComplete}
            >
              Insert
            </StandardButton>
            <StandardButton
              style="gray"
              className="tw-h-[36px] tw-w-[130px]"
              onClick={() => {
                setNoteLLMDraft("");
                getDraftLLMReply(noteResponse);
              }}
              iconLeft="fa fa-arrow-rotate-right"
              loading={loadingAutoComplete}
            >
              Redo
            </StandardButton>
          </div>
        )}
        {!llmDraftPanelOpen && (
          <StandardButton
            style={"gradient-purple"}
            className={
              "tw-m-2 tw-rounded-full tw-border-0 tw-bg-gradient-to-r tw-from-blue-500 tw-to-purple-800 tw-text-white hover:tw-bg-gradient-to-r hover:tw-from-blue-deep-1 hover:tw-to-purple-accent-1"
            }
            onClick={() => {
              getDraftLLMReply(noteResponse);
            }}
            loading={loadingAutoComplete}
            iconLeft="fa fa-magic"
          >
            Generate response
          </StandardButton>
        )}
      </>
    );
  };

  const onEnter = (e: React.KeyboardEvent, callback: () => void) => {
    if (e.key === "Enter") {
      callback();
    }
  };

  return (
    <>
      {!hideModal && (
        <FileUploadCategorizationModal
          setHideModal={() => setHideModal(true)}
          files={files}
          setFiles={setFiles}
          setFilesWithTypes={setFilesWithTypes}
          inputId={`note-reply-file-input-${props.note.postgresId}`}
          hideFileTypeSelection={isVerseOs()}
        />
      )}
      {note.note != null ? (
        <div className={noteStyle} id={`note-${props.note.postgresId}`}>
          {isActiveActionItem && props.isOpsView ? (
            <div className="tw-flex tw-items-center tw-justify-between tw-bg-red-light-1 tw-px-4 tw-py-[10px]">
              <span className="tw-font-karla tw-text-[16px] tw-font-bold tw-text-red-1">
                Needs clinic&apos;s response
              </span>
              <StandardButton
                style="secondary-red-sonner"
                onClick={() => {
                  resolveOrderNote({
                    variables: {
                      noteId: note.postgresId,
                    },
                  })
                    .then((result) => {
                      setNote({
                        ...note,
                        isActionItem: false,
                        opsResolvedAt: new Date().toISOString(),
                        opsResolvedByName: props.opsUserName || "Unknown",
                      });
                      if (props.setOrderRemarks && result.data) {
                        props.setOrderRemarks(
                          result.data.resolveOrderNote.departmentOrderRemarks,
                        );
                      }
                      if (
                        props.setOrderStatus &&
                        result.data?.resolveOrderNote.orderStatus
                      ) {
                        props.setOrderStatus(
                          result.data.resolveOrderNote.orderStatus,
                        );
                      }
                      props.onNoteResolved?.();
                    })
                    .catch((err) => {
                      console.error(err);
                      alert("Failed to resolve note. Please try again.");
                    });
                }}
                disabled={resolving}
                loading={resolving}
              >
                Remove action item for clinic
              </StandardButton>
            </div>
          ) : props.isOpsView && note.clinicNoteThreadResolved ? (
            <div className="tw-flex tw-items-center tw-justify-start tw-border-0 tw-border-b tw-border-solid tw-border-gray-5 tw-bg-gray-light-3 tw-px-4 tw-py-[10px]">
              <span className="tw-font-karla tw-text-[16px] tw-font-medium tw-text-gray-3">
                Thread resolved
              </span>
            </div>
          ) : isManuallyResolved && props.isOpsView ? (
            <div className="tw-flex tw-items-center tw-justify-center tw-border-0 tw-border-b tw-border-solid tw-border-gray-5 tw-bg-gray-light-3 tw-px-4 tw-py-[10px]">
              <span className="tw-font-karla tw-text-[16px] tw-font-medium tw-text-gray-3">
                Action item removed by {note.opsResolvedByName || "Unknown"}
              </span>
            </div>
          ) : isResolvedByClinicResponse && props.isOpsView ? (
            <div className="tw-flex tw-items-center tw-justify-center tw-border-0 tw-border-b tw-border-solid tw-border-gray-5 tw-bg-gray-light-3 tw-px-4 tw-py-[10px]">
              <span className="tw-font-karla tw-text-[16px] tw-font-medium tw-text-gray-3">
                Clinic has responded
              </span>
            </div>
          ) : null}
          <div className="tw-p-[17px]">
            <div className="tw-float-left tw-mr-4 tw-w-[35px]">
              <ProfileSymbol clinicianName={noteDisplayName} />
            </div>
            <div className="tw-mb-3 tw-text-[16px]">
              Order note from
              <b className="tw-ml-2 tw-mr-4">{noteDisplayName}</b>
              {isVerseOs() && note.opsUserName ? `(${note.opsUserName}) ` : " "}
              <span className="tw-text-[12px] tw-text-gray-3">
                {formatDateString(note.createdAt)}
              </span>
            </div>
            {props.isOpsView &&
              note.messageTemplateNames &&
              note.messageTemplateNames.length > 0 && (
                <div className="tw-ml-[45px] tw-mt-2 tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-rounded tw-border tw-border-solid tw-border-gray-5 tw-px-4 tw-py-2">
                  <SelectedTemplateNames names={note.messageTemplateNames} />
                </div>
              )}
            <div
              ref={noteContentRef}
              className="tiptap tw-ml-[45px] tw-mt-4 tw-text-gray-2"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(getRawHTMLFromJSON(note.note)),
              }}
            />
            <div
              ref={lightBoxRef}
              className={`${showLightBox ? "tw-visible tw-opacity-100" : "tw-invisible tw-opacity-0"} transition-opacity tw-fixed tw-left-0 tw-top-0 tw-z-10 tw-flex tw-h-full tw-w-full tw-items-center tw-bg-black/[0.5]`}
              onClick={() => setShowLightBox(false)}
            ></div>
            {note.fileAttachments && note.fileAttachments.length > 0 ? (
              <div className="tw-ml-[45px] tw-mt-[10px]">
                <P2B>Files uploaded</P2B>
                {note.fileAttachments.map((file: any, index: number) => {
                  return (
                    <div key={index} className="tw-my-[5px]">
                      <a href={file.fileUrl}>
                        <i className="fas fa-file-lines tw-mr-[8px]"></i>
                        {file.fileName}
                      </a>{" "}
                      ({prettyFileType(file.fileType)})
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>

          {note.requestedFilesUploaded ? (
            <div className="clearfix tw-min-h-[60px] tw-border-0 tw-border-t-[1px] tw-border-solid tw-border-gray-5 tw-bg-blue-light-1 tw-p-[20px]">
              <i className="fas fa-circle-check tw-float-left tw-mr-[20px] tw-inline-block tw-h-[20px] tw-w-[20px] tw-text-[24px] tw-text-blue-2"></i>
              <P2B className="tw-float-left tw-mb-0 tw-mr-[20px] tw-mt-[2px] tw-inline-block tw-w-[calc(100%-60px)]">
                Documents uploaded.
              </P2B>
            </div>
          ) : filesRequested ? (
            <div className="clearfix tw-min-h-[60px] tw-border-0 tw-border-t-[1px] tw-border-solid tw-border-gray-5 tw-bg-orange-light-1 tw-p-[20px]">
              <i className="fas fa-circle-exclamation tw-float-left tw-mr-[20px] tw-inline-block tw-h-[20px] tw-w-[20px] tw-text-[24px] tw-text-orange-1"></i>
              <P2B className="tw-float-left tw-mb-0 tw-mr-[20px] tw-mt-[2px] tw-inline-block tw-w-[calc(100%-60px)]">
                {uploadRequestString}
              </P2B>
            </div>
          ) : null}

          {noteActions ? (
            <div className="tw-flex tw-w-full tw-flex-row tw-border-0 tw-bg-gray-light-3 tw-px-[17px] tw-py-[12px] tw-align-middle">
              {noteActions}
            </div>
          ) : null}

          {/* Replies thread */}
          <div className="tw-border-0 tw-px-5 tw-py-2">
            {note.responses.length === 0 ? (
              <div className="tw-border-l-1 tw-text-zinc-400 tw-ml-8 tw-mt-2 tw-border-y-0 tw-border-r-0 tw-border-solid tw-border-gray-5 tw-py-2 tw-pl-11 tw-pr-4">
                (No replies for this note yet)
              </div>
            ) : showResponses ? null : (
              <div
                className="tw-border-l-1 tw-ml-8 tw-mt-2 tw-cursor-pointer tw-border-y-0 tw-border-r-0 tw-border-solid tw-border-gray-5 tw-p-1 tw-py-2 tw-pl-11 tw-pr-4 tw-text-blue-1 tw-underline"
                onClick={() => setShowResponses(!showResponses)}
              >
                Show all responses
              </div>
            )}

            {/* Collapsible note replies */}
            {showResponses &&
              note.responses.map((response: any, index: number) => {
                const responseDisplayName =
                  userDisplayLabel(response.clinicianName, response.email) ??
                  "Verse Medical";
                return (
                  <Fragment key={index}>
                    <div className="tw-my-2 tw-p-2">
                      <div className="tw-float-left tw-mr-4 tw-w-[35px]">
                        <ProfileSymbol clinicianName={responseDisplayName} />
                      </div>
                      <div className="tw-px-4 tw-py-2">
                        <b className="tw-mr-4">{responseDisplayName}</b>
                        {isVerseOs() && response.opsUserName
                          ? `(${response.opsUserName}) `
                          : " "}
                        <span className="tw-text-[12px] tw-text-gray-3">
                          {formatDateString(response.createdAt)}
                        </span>
                      </div>
                    </div>
                    <div className="tw-border-l-1 tw-ml-8 tw-border-y-0 tw-border-r-0 tw-border-solid tw-border-gray-5 tw-py-2 tw-pl-11 tw-pr-4 tw-text-gray-2">
                      {response.note}

                      {response.fileAttachments &&
                      response.fileAttachments.length > 0 ? (
                        <div className="tw-mt-[10px]">
                          <P2B>Files uploaded</P2B>
                          {response.fileAttachments.map(
                            (file: any, index: number) => {
                              return (
                                <div key={index} className="tw-my-[5px]">
                                  <a href={file.fileUrl}>
                                    <i className="fas fa-file-lines tw-mr-[8px]"></i>
                                    {file.fileName}
                                  </a>{" "}
                                  ({prettyFileType(file.fileType)})
                                </div>
                              );
                            },
                          )}
                        </div>
                      ) : null}
                    </div>
                  </Fragment>
                );
              })}
          </div>
          {addComment ? (
            <div className="clearfix tw-px-[17px] tw-py-[8px]">
              <div className="tw-relative tw-flex tw-flex-row">
                <div className="tw-w-[36px]">
                  <ProfileSymbol
                    clinicianName={
                      props.currentUserInfo?.userSettings?.name || "??"
                    }
                  />
                </div>
                <div className="tw-border-1 tw-ml-[10px] tw-w-full tw-rounded-md tw-border-solid tw-border-gray-5 tw-bg-gray-light-3 tw-text-gray-2 focus:tw-h-[120px]">
                  <div className="tw-h-180px">
                    {isVerseOs() && draftMessage()}
                    <textarea
                      className="tw-relative tw-h-[120px] tw-w-full tw-resize-none tw-border-0 tw-bg-transparent tw-p-2"
                      placeholder="Add a comment..."
                      value={noteResponse}
                      onChange={(e) => setNoteResponse(e.target.value)}
                    />
                  </div>

                  <div className="tw-mx-[10px]">
                    {Object.keys(filesWithTypes).length > 0 ? (
                      <>
                        <P1B className="tw-pb-[10px]">Files attached</P1B>

                        {Object.keys(filesWithTypes).map((key) => {
                          return (
                            <React.Fragment key={key}>
                              <P2 className="tw-py-[5px]">{key}</P2>

                              {filesWithTypes[key].map((file, index) => {
                                return (
                                  <SelectedFile
                                    key={index}
                                    file={file}
                                    onClick={() => {
                                      const copy = cloneDeep(filesWithTypes);
                                      copy[key].splice(index, 1);
                                      if (copy[key].length === 0) {
                                        delete copy[key];
                                      }
                                      setFilesWithTypes(copy);
                                    }}
                                    className="tw-w-[240px]"
                                  />
                                );
                              })}
                            </React.Fragment>
                          );
                        })}
                      </>
                    ) : null}
                  </div>

                  <div className="clearfix tw-h-[52px]">
                    <label
                      htmlFor={`note-reply-file-input-${props.note.postgresId}`}
                      className="tw-relative !tw-m-0 tw-inline-block"
                    >
                      <input
                        id={`note-reply-file-input-${props.note.postgresId}`}
                        type="file"
                        className="!tw-hidden"
                        onChange={handleFileSelection}
                        onClick={(event) => {
                          // allows selecting the same file again if we dismiss popups
                          event.currentTarget.value = "";
                        }}
                        multiple
                      />
                      <div
                        className={`tw-float-left tw-mx-[10px] tw-my-[8px] tw-box-border tw-h-[36px] tw-cursor-pointer tw-rounded-[4px] tw-border-[1px] tw-border-solid tw-border-gray-light-2 tw-bg-gray-light-2 tw-p-[8px] tw-font-dm-sans tw-font-karla tw-text-[14px] tw-font-bold tw-text-gray-1 hover:tw-border-gray-light-0 hover:tw-bg-gray-light-0 hover:tw-text-gray-1 ${filesRequested && !note.requestedFilesUploaded ? "tw-bg-orange-1 tw-text-white hover:tw-bg-orange-0 hover:tw-text-white" : ""} `}
                      >
                        <i className="fas fa-paperclip tw-mr-[8px]" />
                        Add Files
                      </div>
                    </label>
                    {props.isOpsView === true && (
                      <label className="tw-float-left tw-mx-[10px] tw-my-[8px] tw-flex tw-h-[36px] tw-items-center">
                        <input
                          type="checkbox"
                          checked={requireClinicResponse}
                          onChange={(e) =>
                            setRequireClinicResponse(e.target.checked)
                          }
                          onKeyDown={(e) =>
                            onEnter(e, () =>
                              setRequireClinicResponse(!requireClinicResponse),
                            )
                          }
                          className="tw-mr-2"
                        />
                        Require clinic to respond?
                      </label>
                    )}

                    <div className="tw-float-right tw-mr-[10px] tw-mt-[8px]">
                      <StandardButton
                        style="primary-blue"
                        className="tw-h-[36px]"
                        onClick={() => {
                          submitNote(noteResponse);
                        }}
                        disabled={
                          (noteResponse.length === 0 &&
                            Object.keys(filesWithTypes).length === 0) ||
                          sending
                        }
                        loading={sending}
                      >
                        {sending ? "Sending" : "Send"}
                      </StandardButton>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // Empty div for spacing at the bottom
            <div className="tw-py-[3px]"></div>
          )}
          {/* Actions bar below the response box: "Resolve thread" on the left
              (VerseOS only; templated notes have a clinic-note DAG node whose
              task this completes) and the quick-reply cluster on the right.
              The outer div's pl-[63px]/pr-[17px] lines the framed bar up with the
              response box frame above (17px note pad + 36px avatar + 10px gap). */}
          {addComment &&
            (() => {
              // Shown whenever the thread actually has a worklist task, until
              // resolved. Templates used to stand in for "has a node", but
              // clinic-initiated threads and free-text ops messages get one too
              // and neither carries templates — so ask about the node directly.
              const showResolveThread =
                props.isOpsView &&
                !note.clinicNoteThreadResolved &&
                !!note.hasClinicNoteNode;
              // Quick reply: hidden while required files are outstanding.
              const showQuickReply =
                !filesRequested || note.requestedFilesUploaded;
              if (!showResolveThread && !showQuickReply) return null;

              const resolveThreadButton = showResolveThread && (
                <StandardButton
                  style="secondary-blue"
                  className="tw-h-[36px] tw-border-blue-1 tw-bg-blue-light-1"
                  iconLeft="fas fa-circle-check"
                  disabled={resolvingThread}
                  loading={resolvingThread}
                  onClick={() => {
                    resolveClinicNoteThread({
                      variables: {
                        orderNumber: props.orderNumber,
                        noteId: note.postgresId,
                      },
                      onCompleted: (data) => {
                        // Only flip to "resolved" if the server actually resolved
                        // a node — a note with no node no-ops server-side, so we
                        // must not show the "Thread resolved" banner for it.
                        if (data.resolveClinicNoteThread) {
                          setNote({ ...note, clinicNoteThreadResolved: true });
                          props.onNoteResolved?.();
                        }
                      },
                      onError: () =>
                        alert("Failed to resolve thread. Please try again."),
                    });
                  }}
                >
                  {resolvingThread ? "Resolving" : "Resolve thread"}
                </StandardButton>
              );

              const quickReply = showQuickReply && (
                <div className="tw-flex tw-flex-row tw-items-center tw-gap-2">
                  {/* "or" only makes sense after the Resolve control. */}
                  {showResolveThread && (
                    <span className="tw-whitespace-nowrap">or</span>
                  )}
                  {props.renderQuickReplyButton ? (
                    props.renderQuickReplyButton({
                      onRespond: () => submitNote(thumbs_up),
                      children: <>Respond with {thumbs_up}</>,
                    })
                  ) : (
                    <StandardButton
                      style="secondary-blue"
                      className="tw-h-[40px]"
                      onClick={() => submitNote(thumbs_up)}
                    >
                      Respond with {thumbs_up}
                    </StandardButton>
                  )}
                </div>
              );

              // VerseOS gets the framed action bar (Resolve thread + quick reply).
              // The clinic site (VerseMedical) only ever has the quick reply, and
              // shows it plain — no frame — as it did before this feature.
              if (!props.isOpsView) {
                return (
                  <div className="tw-mt-[8px] tw-flex tw-justify-end tw-pr-[17px]">
                    {quickReply}
                  </div>
                );
              }
              return (
                <div className="tw-mt-[8px] tw-pl-[63px] tw-pr-[17px]">
                  <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-rounded-[8px] tw-border tw-border-solid tw-border-gray-light-1 tw-bg-gray-light-3 tw-px-[8px] tw-py-[4px]">
                    {resolveThreadButton || <div />}
                    {quickReply}
                  </div>
                </div>
              );
            })()}
          {/* Diplay errors on sending a note */}
          {responseErrorMessage.length > 0 ? (
            <div className="tw-px-[17px] tw-pb-[8px] tw-pt-[3px] tw-text-red-1">
              {responseErrorMessage}
            </div>
          ) : null}
          {opsOptions}
        </div>
      ) : null}
    </>
  );
}

/**
 * Displays a profile picture/symbol using the user's intials
 * @param props accepts username
 * @returns rounded rectangle with user's initials
 */
export const ProfileSymbol = (props: { clinicianName: string }) => {
  const initials = getInitials(props.clinicianName);
  const isVerseMedical = props.clinicianName == "Verse Medical";
  return (
    <div
      className={`tw-inline-block tw-h-[33px] tw-w-[33px] tw-rounded-md tw-text-center tw-align-middle tw-text-[16px] tw-font-bold tw-leading-[33px] ${isVerseMedical ? "tw-bg-blue-2 tw-text-white" : "tw-bg-blue-light-1 tw-text-blue-1"}`}
    >
      {initials}
    </div>
  );
};
